import { Box } from "@mui/material"
import {
  axisClasses,
  ChartsReferenceLine,
  ChartsTooltipContainer,
  LineChart,
  type LineSeries,
} from "@mui/x-charts-pro"
import { useMemo, useState } from "react"
import { dayjsUTC } from "../../utils/dates"
import { extraColors } from "../../utils/theme"
import type { DatasetEntry } from "../Wastewater/timeSeriesConstants"
import { useMonthBoundaries } from "../Wastewater/useWastewaterDates"
import { WASTEWATER_CHART_COLORS } from "../Wastewater/wastewaterHeatmapConstants"

const FADED_SERIES_OPACITY = 0.3
/** MUI default is 300ms; shorter feels snappier on series hover fade. */
const FADE_HOVER_TRANSITION_MS = 100
/** Bracket keys avoid TS parsing `MuiLineChart-line` as subtraction in `sx`. */
const lineChartFadeHoverSx = {
  '& [data-faded="true"]': {
    opacity: `${FADED_SERIES_OPACITY} !important`,
  },
  [`& .MuiLineChart-linePlot .MuiLineChart-line`]: {
    transitionDuration: `${FADE_HOVER_TRANSITION_MS}ms`,
  },
  [`& .MuiLineChart-markAnimate`]: {
    transitionDuration: `${FADE_HOVER_TRANSITION_MS}ms`,
  },
} as const

const buildInitialHiddenIds = (
  series: LineSeries[],
  maxVisibleSeries?: number,
  defaultVisibleSeries?: number
): Set<string> => {
  if (!maxVisibleSeries) return new Set()
  const initialVisible = Math.min(
    defaultVisibleSeries ?? maxVisibleSeries,
    maxVisibleSeries,
    series.length
  )
  if (series.length <= initialVisible) return new Set()
  return new Set(series.slice(initialVisible).map(s => String(s.id)))
}

interface LegendRenderProps<TLegendItem> {
  items: TLegendItem[]
  hiddenIds: Set<string>
  onToggle: (id: string) => void
  onToggleAll: () => void
  visibleCount: number
  atVisibleCap: boolean
}

interface TimeSeriesChartProps<TLegendItem> {
  dataset: DatasetEntry[]
  dates: Date[]
  series: LineSeries[]
  legendItems: TLegendItem[]
  yAxisLabel: string
  chartHeight?: number
  /** Nudge the y-axis title left (px) without shrinking the plot area. */
  yAxisLabelOffset?: number
  /** Format left-axis tick labels (e.g. scientific notation for large values). */
  yAxisValueFormatter?: (value: number) => string
  yAxisTickFontSize?: number
  /** Chart left of legend (default) or stacked with legend below. */
  legendLayout?: "beside" | "below"
  /** Max series shown at once. */
  maxVisibleSeries?: number
  /** How many series are visible on first load (defaults to `maxVisibleSeries`). */
  defaultVisibleSeries?: number
  /** On mark hover, emphasize that series and fade all others. */
  fadeOtherSeriesOnHover?: boolean
  renderLegend: (props: LegendRenderProps<TLegendItem>) => React.ReactNode
  renderTooltip: (hiddenIds: Set<string>) => React.ReactNode
}

export const TimeSeriesChart = <TLegendItem,>({
  dataset,
  dates,
  series,
  legendItems,
  yAxisLabel,
  chartHeight = 400,
  yAxisLabelOffset = 0,
  yAxisValueFormatter,
  yAxisTickFontSize,
  legendLayout = "beside",
  maxVisibleSeries,
  defaultVisibleSeries,
  fadeOtherSeriesOnHover = false,
  renderLegend,
  renderTooltip,
}: TimeSeriesChartProps<TLegendItem>) => {
  const seriesKey = useMemo(
    () => series.map(s => String(s.id)).join("|"),
    [series]
  )

  return (
    <TimeSeriesChartBody
      key={seriesKey}
      dataset={dataset}
      dates={dates}
      series={series}
      legendItems={legendItems}
      yAxisLabel={yAxisLabel}
      chartHeight={chartHeight}
      yAxisLabelOffset={yAxisLabelOffset}
      yAxisValueFormatter={yAxisValueFormatter}
      yAxisTickFontSize={yAxisTickFontSize}
      legendLayout={legendLayout}
      maxVisibleSeries={maxVisibleSeries}
      defaultVisibleSeries={defaultVisibleSeries}
      fadeOtherSeriesOnHover={fadeOtherSeriesOnHover}
      renderLegend={renderLegend}
      renderTooltip={renderTooltip}
    />
  )
}

const TimeSeriesChartBody = <TLegendItem,>({
  dataset,
  dates,
  series,
  legendItems,
  yAxisLabel,
  chartHeight = 400,
  yAxisLabelOffset = 0,
  yAxisValueFormatter,
  yAxisTickFontSize = 9,
  legendLayout = "beside",
  maxVisibleSeries,
  defaultVisibleSeries,
  fadeOtherSeriesOnHover = false,
  renderLegend,
  renderTooltip,
}: TimeSeriesChartProps<TLegendItem>) => {
  const [hiddenIds, setHiddenIds] = useState(() =>
    buildInitialHiddenIds(series, maxVisibleSeries, defaultVisibleSeries)
  )

  const { startDates, monthCenters } = useMonthBoundaries(dates)
  const axisMin = dates[0]?.getTime()
  const axisMax = dates[dates.length - 1]?.getTime()

  const visibleStartDates = useMemo(
    () =>
      startDates.filter(
        t =>
          axisMin == null || axisMax == null || (t >= axisMin && t <= axisMax)
      ),
    [startDates, axisMin, axisMax]
  )

  const visibleMonthCenters = useMemo(
    () =>
      monthCenters.filter(
        m =>
          axisMin == null ||
          axisMax == null ||
          (m.x >= axisMin && m.x <= axisMax)
      ),
    [monthCenters, axisMin, axisMax]
  )

  const visibleSeries = useMemo((): LineSeries[] => {
    const shown = series.filter(s => !hiddenIds.has(String(s.id)))
    if (!fadeOtherSeriesOnHover) return shown
    return shown.map(s => ({
      ...s,
      highlightScope: {
        highlight: "series",
        fade: "global",
      },
    }))
  }, [series, hiddenIds, fadeOtherSeriesOnHover])

  const visibleCount = visibleSeries.length
  const atVisibleCap =
    maxVisibleSeries != null && visibleCount >= maxVisibleSeries

  const handleToggle = (id: string) => {
    setHiddenIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        if (
          maxVisibleSeries != null &&
          series.filter(s => !prev.has(String(s.id))).length >= maxVisibleSeries
        ) {
          return prev
        }
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleToggleAll = () => {
    const allIds = series.map(s => String(s.id))
    if (!maxVisibleSeries) {
      if (hiddenIds.size > 0) {
        setHiddenIds(new Set())
      } else {
        setHiddenIds(new Set(allIds))
      }
      return
    }
    if (visibleCount > 0) {
      setHiddenIds(new Set(allIds))
    } else {
      setHiddenIds(
        new Set(series.slice(maxVisibleSeries).map(s => String(s.id)))
      )
    }
  }

  const legendBelow = legendLayout === "below"

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: legendBelow ? "column" : "row",
        alignItems: legendBelow ? "stretch" : "flex-start",
        width: "100%",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: legendBelow ? "100%" : undefined,
        }}
      >
        <LineChart
          height={chartHeight}
          dataset={dataset}
          series={visibleSeries}
          hideLegend={true}
          slotProps={{ tooltip: { trigger: "none" } }}
          xAxis={[
            {
              min: axisMin ?? startDates[0],
              max: axisMax,
              id: "dateAxis",
              dataKey: "date",
              scaleType: "utc",
              disableTicks: false,
              tickSize: 16,
              tickInterval: visibleStartDates,
              tickLabelStyle: { display: "none" },
              valueFormatter: (v: Date) => dayjsUTC(v).format("MMM DD"),
            },
            {
              min: axisMin ?? startDates[0],
              max: axisMax,
              id: "labelAxis",
              dataKey: "date",
              scaleType: "utc",
              disableLine: true,
              disableTicks: true,
              position: "bottom",
              tickInterval: visibleMonthCenters.map(m => m.x),
              valueFormatter: (value: Date | number) => {
                const valTime = value instanceof Date ? value.getTime() : value
                const match = visibleMonthCenters.find(
                  m => Math.abs(m.x - valTime) < 5 * 24 * 60 * 60 * 1000
                )
                return match ? match.label : ""
              },
              tickLabelStyle: {
                fill: WASTEWATER_CHART_COLORS.text,
                fontSize: 12,
                fontWeight: 600,
                textAnchor: "middle",
                dominantBaseline: "text-after-edge",
              },
            },
          ]}
          yAxis={[
            {
              label: yAxisLabel,
              labelStyle: {
                fill: WASTEWATER_CHART_COLORS.text,
                fontSize: 9,
                fontWeight: 600,
              },
              tickLabelStyle: {
                fill: WASTEWATER_CHART_COLORS.text,
                fontSize: yAxisTickFontSize,
                fontWeight: 600,
              },
              ...(yAxisValueFormatter && {
                valueFormatter: yAxisValueFormatter,
              }),
            },
          ]}
          grid={{ horizontal: true }}
          sx={{
            [`.${axisClasses.line}`]: {
              stroke: extraColors.disabled,
            },
            [`.${axisClasses.tick}`]: {
              stroke: extraColors.disabled,
            },
            ...(yAxisLabelOffset !== 0 && {
              [`.${axisClasses.left} .${axisClasses.label}`]: {
                transform: `translateX(-${yAxisLabelOffset}px)`,
                transformOrigin: "center center",
              },
            }),
            ...(fadeOtherSeriesOnHover && lineChartFadeHoverSx),
          }}
        >
          {visibleStartDates.map(
            (dateMs, i) =>
              i !== 0 && (
                <ChartsReferenceLine
                  key={dateMs}
                  x={new Date(dateMs)}
                  axisId="dateAxis"
                  lineStyle={{ stroke: extraColors.disabled, strokeWidth: 1 }}
                />
              )
          )}
          <ChartsTooltipContainer placement="auto">
            {renderTooltip(hiddenIds)}
          </ChartsTooltipContainer>
        </LineChart>
      </Box>
      <Box sx={legendBelow ? { width: "100%", mt: 2 } : undefined}>
        {renderLegend({
          items: legendItems,
          hiddenIds,
          onToggle: handleToggle,
          onToggleAll: handleToggleAll,
          visibleCount,
          atVisibleCap,
        })}
      </Box>
    </Box>
  )
}
